import { CallApi, handleJSONResponse } from "./common";
import { SimpleRelease } from "../../../../common/types/release/simple-release";
import { ReleaseStatus } from "../../../../common/types/release/relase-status";
import { PublishedRelease } from "../../../../common/types/release/published-release";
import { PageResult } from "../../../../common/types/api/page-result";
import { ReleaseBackofficeStatus } from "../../../../common/types/release/release-backoffice-status";
import SimpleTrack from "../../../../common/types/release/simple-track";
import { AdminReleaseEditPayload } from "../../../../services/release/types/api/admin/release-edit-payload";
import { Collaborator } from "../../../../services/collaborator/types/collaborator";
import { IngestionMessage } from "../../../../services/publisher-notifications/types/ingestion-message";
import { ReleaseStore } from "../../../../common/types/release/release-store";

const callAPI = CallApi("releases");

export const listByStatus = async (
  status: ReleaseStatus,
  next?: string
): Promise<PageResult<SimpleRelease>> => {
  const url = `/status/${status}?page=${next || ""}`;

  const response = await callAPI(url, "GET");

  return handleJSONResponse(response);
};

export const getPublished = async (id: string) => {
  const url = `/published/${id}`;

  const response = await callAPI(url, "GET");

  return await response.json();
};

export const edit = async (
  releaseId: string,
  release: Partial<SimpleRelease>,
  tracks: SimpleTrack[],
  collaborators: Collaborator[]
): Promise<PublishedRelease> => {
  const data: AdminReleaseEditPayload = {
    releaseId,
    release,
    tracks,
    collaborators,
  };

  const response = await callAPI(`/admin/edit`, "PUT", data);

  return handleJSONResponse(response);
};

export const reprocess = async (
  releaseId: string
): Promise<PublishedRelease> => {
  const data = {
    releaseId,
  };

  const response = await callAPI(`/admin/reprocess`, "PUT", data);

  return handleJSONResponse(response);
};

export const listMessages = async (
  upcean: string
): Promise<IngestionMessage[]> => {
  const response = await callAPI(`/admin/messages/${upcean}`, "GET");

  const results: IngestionMessage[] = await handleJSONResponse(response);

  return results.map((item) => ({ ...item, date: new Date(item.date) }));
};

export const _changeStatus = async (
  action: ReleaseBackofficeStatus,
  releaseId: string,
  upcCode: string,
  key: string = "",
  hasVideo: boolean = false,
  coverUrl: string = "",
  title: string = "",
  reasons?: string
): Promise<PublishedRelease> => {
  const data = {
    releaseId,
    upcCode,
    key,
    hasVideo,
    coverUrl,
    title,
    reasons,
  };

  const response = await callAPI(`/admin/${action}`, "POST", data);

  return handleJSONResponse(response);
};

export const approve = async (
  releaseId: string,
  upcCode: string,
  key: string,
  hasVideo: boolean,
  coverUrl: string,
  title: string
): Promise<PublishedRelease> => {
  return _changeStatus(
    "accepted",
    releaseId,
    upcCode,
    key,
    hasVideo,
    coverUrl,
    title
  );
};

export const reject = async (
  releaseId: string,
  upcCode: string,
  key: string,
  hasVideo: boolean,
  coverUrl: string,
  title: string,
  message: string
): Promise<PublishedRelease> => {
  return _changeStatus(
    "rejected",
    releaseId,
    upcCode,
    key,
    hasVideo,
    coverUrl,
    title,
    message
  );
};

export const requestEdit = async (
  releaseId: string,
  upcCode: string,
  key: string,
  hasVideo: boolean,
  coverUrl: string,
  title: string,
  message: string
): Promise<PublishedRelease> => {
  return _changeStatus(
    "request-changes",
    releaseId,
    upcCode,
    key,
    hasVideo,
    coverUrl,
    title,
    message
  );
};

export const takedown = async (releaseId: string, upcCode: string) => {
  return _changeStatus("takedown", releaseId, upcCode);
};

export const getArtistReleases = async (
  artistId: string
): Promise<SimpleRelease[]> => {
  const response = await callAPI(`/admin/list/${artistId}?status=all`, "GET");

  return handleJSONResponse(response);
};

export const updateDelivery = async (
  releaseId: string,
  instruction: 'redeliver' | 'takedown',
  stores: ReleaseStore[]
): Promise<ReleaseStore[]> => {
  const response = await callAPI(`/admin/update-delivery`, "POST", {releaseId, instruction, stores});

  return handleJSONResponse(response);
}